import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './ReverseMortgage.css';

const ReverseMortgage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);
  const [statsBackgroundImage, setStatsBackgroundImage] = useState<string>('');
  const [heroImage, setHeroImage] = useState<string>('');
  const [whyChooseImage, setWhyChooseImage] = useState<string>('');
  const [eligibleImage, setEligibleImage] = useState<string>('');
  const [costsImage, setCostsImage] = useState<string>('');
  const [contactAvatarImage, setContactAvatarImage] = useState<string>('');
  
  const defaultStatsImage = 'https://api.builder.io/api/v1/image/assets/TEMP/46b7939e5bebf1b51c9bf6b1599f55e6c8e8dbea?width=2880';
  const defaultPlaceholder = 'https://api.builder.io/api/v1/image/assets/TEMP/placeholder';

  useEffect(() => {
    // Load stats background image
    const statsImg = new Image();
    statsImg.src = '/assets/reverse-mortgage/stats/cta.jpg';
    statsImg.onload = () => setStatsBackgroundImage(`url(${statsImg.src})`);
    statsImg.onerror = () => setStatsBackgroundImage(`url(${defaultStatsImage})`);

    // Load hero image
    const heroImg = new Image();
    heroImg.src = '/assets/reverse-mortgage/hero/reverse-mortgage.jpg';
    heroImg.onload = () => setHeroImage(heroImg.src);
    heroImg.onerror = () => setHeroImage(defaultPlaceholder);

    // Load why choose image
    const whyImg = new Image();
    whyImg.src = '/assets/reverse-mortgage/why-choose/house.jpg';
    whyImg.onload = () => setWhyChooseImage(whyImg.src);
    whyImg.onerror = () => setWhyChooseImage(defaultPlaceholder);

    // Load eligible image
    const eligibleImg = new Image();
    eligibleImg.src = '/assets/reverse-mortgage/eligible/house.jpg';
    eligibleImg.onload = () => setEligibleImage(eligibleImg.src);
    eligibleImg.onerror = () => setEligibleImage(defaultPlaceholder);

    // Load costs image
    const costsImg = new Image();
    costsImg.src = '/assets/reverse-mortgage/costs/costs.png';
    costsImg.onload = () => setCostsImage(costsImg.src);
    costsImg.onerror = () => setCostsImage(defaultPlaceholder);

    // Load contact avatar image
    const contactImg = new Image();
    contactImg.src = '/assets/reverse-mortgage/faq-contact/contact.jpg';
    contactImg.onload = () => setContactAvatarImage(contactImg.src);
    contactImg.onerror = () => {
      const altImg = new Image();
      altImg.src = '/assets/reverse-mortgage/faq-contact/avatar.jpg';
      altImg.onload = () => setContactAvatarImage(altImg.src);
      altImg.onerror = () => setContactAvatarImage(defaultPlaceholder);
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: 'How do I qualify for a reverse mortgage?',
      answer: 'To qualify, you must be at least 62 years old, own a home with significant equity, and use the property as your primary residence. You also need to be able to maintain the home and keep up with property taxes and insurance.'
    },
    {
      question: 'Will I still own my home with a reverse mortgage?',
      answer: 'Yes, you remain the owner of your home. The reverse mortgage allows you to access your home\'s equity without selling it, and you can continue to live there as long as you meet the loan requirements.'
    },
    {
      question: 'How is a reverse mortgage repaid?',
      answer: 'The loan is repaid when you sell the home, move out permanently, or pass away. At that time, the home is usually sold to cover the loan balance, and any remaining equity goes to you or your heirs.'
    },
    {
      question: 'Can I leave my home to my heirs with a reverse mortgage?',
      answer: 'Yes, your heirs can inherit the home. They will have the option to repay the reverse mortgage and keep the home or sell it to pay off the loan. Any remaining equity after the loan is repaid belongs to your heirs.'
    },
    {
      question: 'What are the costs involved in a reverse mortgage?',
      answer: 'The costs include origination fees, appraisal fees, closing costs, and mortgage insurance. These fees can often be rolled into the loan, so you don\'t have to pay them out of pocket upfront.'
    }
  ];

  return (
    <div className="reverse-mortgage-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="reverse-hero">
          <div className="reverse-container">
            <div className="reverse-hero-content">
              <div className="reverse-hero-text">
                <h1 className="reverse-hero-title">
                  Turn your home into income without selling
                </h1>
                <p className="reverse-hero-description">
                  Gain financial freedom in retirement by turning your home equity into cash without selling your property.
                </p>
                <div className="reverse-hero-ctas">
                  <button className="reverse-cta-primary">Check your eligibility</button>
                  <button className="reverse-cta-secondary">Contact us</button>
                </div>
                <p className="reverse-trust-line">
                  Trusted by <strong>over 500 homeowners</strong> who have successfully accessed their home equity for a better retirement.
                </p>
              </div>
              <div className="reverse-hero-image">
                <img 
                  src={heroImage || defaultPlaceholder} 
                  alt="Reverse mortgage" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Partner Logos Section */}
        <section className="reverse-partners">
          <div className="reverse-container">
            <div className="reverse-partners-grid">
              <div className="reverse-partner-card">
                <img src="https://api.builder.io/api/v1/image/assets/TEMP/548487c4b41fbe61af6e61e31aff9a2149089bfb?width=608" alt="Sierra Pacific Mortgage" />
              </div>
              <div className="reverse-partner-card">
                <img src="https://api.builder.io/api/v1/image/assets/TEMP/6e0dd72837563fabb3a036993de374e709a69fb2?width=527" alt="AmiVest" />
              </div>
              <div className="reverse-partner-card">
                <img src="https://api.builder.io/api/v1/image/assets/TEMP/1b42400f0879d4e23c30a78eea62d6e536563b76?width=567" alt="Advancial" />
              </div>
            </div>
          </div>
        </section>

        {/* 4-Step Reverse Mortgage Process Section */}
        <section className="reverse-process">
          <div className="reverse-container">
            <div className="reverse-process-header">
              <h2 className="reverse-section-title">Simple 4-step reverse mortgage process</h2>
              <p className="reverse-section-description">
                A reverse mortgage allows homeowners to convert a portion of their home's equity into cash. Unlike a traditional mortgage, there are no monthly payments. Instead, the loan is repaid when you sell the home, move out, or pass away.
              </p>
            </div>
            <div className="reverse-steps-grid">
              <div className="reverse-step-card">
                <div className="reverse-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4L30 18L44 20L32 30L34 44L24 36L14 44L16 30L4 20L18 18L24 4Z" fill="#0578EC"/>
                  </svg>
                </div>
                <h3 className="reverse-step-title">Initial consultation</h3>
                <p className="reverse-step-description">
                  We'll assess your situation and explain the reverse mortgage options available to you.
                </p>
              </div>
              <div className="reverse-step-card">
                <div className="reverse-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 24L20 12L28 20L40 8" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M28 8H40V20" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="reverse-step-title">Eligibility check</h3>
                <p className="reverse-step-description">
                  We'll review your home's value and financial standing to confirm your eligibility for a reverse mortgage.
                </p>
              </div>
              <div className="reverse-step-card">
                <div className="reverse-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4C12.954 4 4 12.954 4 24C4 35.046 12.954 44 24 44C35.046 44 44 35.046 44 24C44 12.954 35.046 4 24 4Z" stroke="#0578EC" strokeWidth="2"/>
                    <path d="M24 16V24L28 28" stroke="#0578EC" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="reverse-step-title">Choose your payment plan</h3>
                <p className="reverse-step-description">
                  Decide how you want to receive your funds—lump sum, monthly payments, or line of credit.
                </p>
              </div>
              <div className="reverse-step-card">
                <div className="reverse-step-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 24L20 12L28 20L40 8" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="reverse-step-title">Close and access funds</h3>
                <p className="reverse-step-description">
                  Once the paperwork is completed, you'll receive your funds and enjoy financial flexibility in retirement.
                </p>
              </div>
            </div>
            <div className="reverse-process-cta">
              <button className="reverse-cta-primary">Get started today</button>
            </div>
          </div>
        </section>

        {/* Why choose a reverse mortgage section */}
        <section className="reverse-why">
          <div className="reverse-container">
            <div className="reverse-why-content">
              <div className="reverse-why-text">
                <h2 className="reverse-section-title">Why choose a reverse mortgage?</h2>
                <div className="reverse-benefits-list">
                  <div className="reverse-benefit-item">
                    <div className="reverse-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Stay in your home:</strong>{' '}
                      Continue living in your home while receiving payments based on its value.
                    </p>
                  </div>
                  <div className="reverse-benefit-item">
                    <div className="reverse-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>No monthly payments:</strong>{' '}
                      You don't need to make monthly mortgage payments as long as you remain in the home.
                    </p>
                  </div>
                  <div className="reverse-benefit-item">
                    <div className="reverse-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Access tax-free cash:</strong>{' '}
                      Use the funds for any purpose, whether it's healthcare, home improvements, or travel.
                    </p>
                  </div>
                  <div className="reverse-benefit-item">
                    <div className="reverse-star-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                      </svg>
                    </div>
                    <p>
                      <strong>Flexible disbursement:</strong>{' '}
                      Choose how you receive your money—lump sum, monthly payments, or line of credit.
                    </p>
                  </div>
                </div>
                <div className="reverse-why-ctas">
                  <button className="reverse-cta-primary">Get your rate</button>
                  <button className="reverse-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
              <div className="reverse-why-image">
                <img 
                  src={whyChooseImage || defaultPlaceholder} 
                  alt="Why choose reverse mortgage" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Are you eligible section */}
        <section className="reverse-eligible">
          <div className="reverse-container">
            <div className="reverse-eligible-content">
              <div className="reverse-eligible-image">
                <img 
                  src={eligibleImage || defaultPlaceholder} 
                  alt="Eligible for reverse mortgage" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
              <div className="reverse-eligible-text">
                <h2 className="reverse-section-title">Are you eligible for a reverse mortgage?</h2>
                <div className="reverse-eligible-criteria">
                  <p className="reverse-criteria-intro">
                    To qualify for a reverse mortgage, you need to meet specific criteria:
                  </p>
                  <div className="reverse-benefits-list">
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>Be at least 62 years old</p>
                    </div>
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>Own a home with significant equity</p>
                    </div>
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>The home must be your primary residence</p>
                    </div>
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>Be able to maintain the property and keep up with taxes and insurance</p>
                    </div>
                  </div>
                  <p className="reverse-criteria-note">
                    We can help you assess your eligibility and guide you through the application process.
                  </p>
                </div>
                <div className="reverse-eligible-ctas">
                  <button className="reverse-cta-primary">Get your rate</button>
                  <button className="reverse-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What are the costs section */}
        <section className="reverse-costs">
          <div className="reverse-container">
            <div className="reverse-costs-content">
              <div className="reverse-costs-text">
                <h2 className="reverse-section-title">What are the costs of a reverse mortgage?</h2>
                <div className="reverse-costs-info">
                  <p className="reverse-costs-intro">
                    While a reverse mortgage allows you to tap into your home's equity, there are some associated costs:
                  </p>
                  <div className="reverse-benefits-list">
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>
                        <strong>Origination Fees:</strong>{' '}
                        Charged by the lender for processing the loan.
                      </p>
                    </div>
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>
                        <strong>Appraisal Fee:</strong>{' '}
                        To determine the value of your home.
                      </p>
                    </div>
                    <div className="reverse-benefit-item">
                      <div className="reverse-star-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#0578EC"/>
                        </svg>
                      </div>
                      <p>
                        <strong>Closing Costs:</strong>{' '}
                        These can include title insurance, attorney fees, and other administrative costs.
                      </p>
                    </div>
                  </div>
                  <p className="reverse-costs-note">
                    We'll provide a detailed breakdown of costs to help you make an informed decision.
                  </p>
                </div>
                <div className="reverse-costs-ctas">
                  <button className="reverse-cta-primary">Get your rate</button>
                  <button className="reverse-cta-secondary">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Contact us
                  </button>
                </div>
              </div>
              <div className="reverse-costs-image">
                <img 
                  src={costsImage || defaultPlaceholder} 
                  alt="Reverse mortgage costs" 
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = defaultPlaceholder;
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="reverse-stats">
          <div className="reverse-stats-wrapper">
            <div 
              className="reverse-stats-image"
              style={{
                backgroundImage: statsBackgroundImage || undefined,
                backgroundSize: statsBackgroundImage ? 'cover' : undefined,
                backgroundPosition: statsBackgroundImage ? 'center' : undefined,
                backgroundRepeat: statsBackgroundImage ? 'no-repeat' : undefined
              }}
            >
              <div className="reverse-stats-image-fade"></div>
            </div>
            <div className="reverse-stats-content-wrapper">
              <div className="reverse-container">
                <div className="reverse-stats-content">
                  <div className="reverse-stats-text">
                    <h2 className="reverse-stats-title">We will find best rate</h2>
                    <div className="reverse-stats-ctas">
                      <button className="reverse-cta-primary">Get your rate</button>
                      <button className="reverse-cta-secondary">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M6.28521 6.64001C6.80721 7.72723 7.5188 8.74621 8.41998 9.64739C9.32116 10.5486 10.3401 11.2602 11.4274 11.7822C11.5209 11.8271 11.5676 11.8495 11.6268 11.8668C11.8371 11.9281 12.0953 11.884 12.2733 11.7565C12.3234 11.7206 12.3663 11.6778 12.452 11.592C12.7143 11.3298 12.8454 11.1987 12.9772 11.113C13.4744 10.7898 14.1153 10.7898 14.6125 11.113C14.7443 11.1987 14.8754 11.3298 15.1376 11.592L15.2838 11.7382C15.6823 12.1368 15.8816 12.3361 15.9899 12.5501C16.2052 12.9758 16.2052 13.4785 15.9899 13.9041C15.8816 14.1182 15.6823 14.3175 15.2838 14.7161L15.1655 14.8343C14.7683 15.2315 14.5697 15.4301 14.2997 15.5818C14 15.7501 13.5347 15.8711 13.191 15.8701C12.8813 15.8692 12.6696 15.8091 12.2463 15.6889C9.97125 15.0432 7.82448 13.8249 6.0335 12.0339C4.24251 10.2429 3.02415 8.09612 2.37843 5.82108C2.25827 5.39774 2.19819 5.18607 2.19727 4.87636C2.19625 4.5327 2.31727 4.06733 2.48559 3.7677C2.63727 3.49768 2.83588 3.29907 3.2331 2.90185L3.35132 2.78362C3.74991 2.38504 3.9492 2.18575 4.16323 2.07749C4.5889 1.86219 5.0916 1.86219 5.51727 2.07749C5.73131 2.18575 5.9306 2.38504 6.32918 2.78362L6.47533 2.92978C6.73754 3.19198 6.86864 3.32308 6.95436 3.45492C7.2776 3.95208 7.2776 4.59302 6.95435 5.09019C6.86864 5.22202 6.73754 5.35312 6.47533 5.61533C6.3896 5.70106 6.34673 5.74393 6.31085 5.79404C6.18334 5.97211 6.13932 6.2303 6.20061 6.44057C6.21786 6.49974 6.24031 6.5465 6.28521 6.64001Z" stroke="black" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Contact us
                      </button>
                    </div>
                    <div className="reverse-stats-grid">
                      <div className="reverse-stat-item">
                        <div className="reverse-stat-number">17+</div>
                        <div className="reverse-stat-label">years in experience</div>
                      </div>
                      <div className="reverse-stat-item">
                        <div className="reverse-stat-number">128+</div>
                        <div className="reverse-stat-label">helped clients</div>
                      </div>
                      <div className="reverse-stat-item">
                        <div className="reverse-stat-number">96%</div>
                        <div className="reverse-stat-label">satisfaction rate</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="reverse-testimonials">
          <div className="reverse-container">
            <div className="reverse-testimonials-grid">
              <div className="reverse-testimonial">
                <div className="reverse-testimonial-divider">
                  <div className="reverse-testimonial-divider-line"></div>
                </div>
                <div className="reverse-testimonial-content">
                  <div className="reverse-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="reverse-testimonial-author">
                    <strong>Elena Tuttle</strong> <span className="reverse-testimonial-location">from Tustin</span>
                  </p>
                  <p className="reverse-testimonial-text">
                    "Above and beyond. We had a very complex 1031 exchange and multiple mortgages with multiple entities. Stephan and his team worked tirelessly to ensure we closed on our properties"
                  </p>
                </div>
              </div>
              <div className="reverse-testimonial">
                <div className="reverse-testimonial-divider">
                  <div className="reverse-testimonial-divider-line"></div>
                </div>
                <div className="reverse-testimonial-content">
                  <div className="reverse-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="reverse-testimonial-author">
                    <strong>Dan May</strong> <span className="reverse-testimonial-location">from Costa Mesa</span>
                  </p>
                  <p className="reverse-testimonial-text">
                    "My go to resource for everything mortgage related. Prompt service, valuable experience, competitive pricing.  Highly recommend.  You owe it to yourself and family to use The Best!"
                  </p>
                </div>
              </div>
              <div className="reverse-testimonial">
                <div className="reverse-testimonial-content">
                  <div className="reverse-testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9996 17.8441L6.18281 20.9021L7.29371 14.4251L2.58786 9.838L9.09119 8.89301L11.9996 3L14.9079 8.89301L21.4113 9.838L16.7054 14.4251L17.8163 20.9021L11.9996 17.8441Z" fill="#0578EC"/>
                      </svg>
                    ))}
                  </div>
                  <p className="reverse-testimonial-author">
                    <strong>Agent Ivy</strong> <span className="reverse-testimonial-location">from Irvine</span>
                  </p>
                  <p className="reverse-testimonial-text">
                    "His strategic helped us procure a good loan at the best available rate. He took an interest in my concerns and my fears and offered me incredibly kind and helpful feedback."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="reverse-faq">
          <div className="reverse-container">
            <div className="reverse-faq-content">
              <h2 className="reverse-faq-title">
                Have questions?<br />Find answer.
              </h2>
              <div className="reverse-faq-list">
                {faqs.map((faq, index) => (
                  <div key={index} className="reverse-faq-item">
                    <button 
                      className="reverse-faq-question"
                      onClick={() => toggleFAQ(index)}
                    >
                      <span>{faq.question}</span>
                      <svg 
                        className={`reverse-faq-icon ${openFAQ === index ? 'open' : ''}`}
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none"
                      >
                        <path d="M18 15L12 9L6 15" stroke="#0578EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {openFAQ === index && (
                      <div className="reverse-faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="reverse-faq-contact">
                <div className="reverse-faq-contact-avatar">
                  <img 
                    src={contactAvatarImage || defaultPlaceholder} 
                    alt="Contact" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultPlaceholder;
                    }}
                  />
                </div>
                <div className="reverse-faq-contact-info">
                  <h3 className="reverse-faq-contact-title">Still have a questions?</h3>
                  <div className="reverse-faq-contact-details">
                    <div className="reverse-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Mon - Fri, 9am to 7pm</span>
                    </div>
                    <div className="reverse-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.5625 3.75H3.4375C2.57417 3.75 1.875 4.45 1.875 5.3125V14.6875C1.875 15.55 2.575 16.25 3.4375 16.25H16.5625C17.425 16.25 18.125 15.55 18.125 14.6875V5.3125C18.125 4.44917 17.425 3.75 16.5625 3.75Z" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.375 6.25L10 10.625L15.625 6.25" stroke="#0578EC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Valentinoloanofficer@gmail.com</span>
                    </div>
                    <div className="reverse-faq-contact-item">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M17.4999 14.5376C16.8894 13.9222 15.4108 13.0246 14.6928 12.6627C13.7582 12.1917 13.6811 12.154 12.9467 12.6996C12.4569 13.064 12.1311 13.3889 11.5576 13.2666C10.9832 13.1444 9.73762 12.4543 8.64631 11.3671C7.555 10.2791 6.82555 8.99658 6.70329 8.42549C6.58021 7.85358 6.91089 7.53111 7.27192 7.04043C7.78065 6.34872 7.74209 6.23303 7.3072 5.29844C6.96832 4.57145 6.04358 3.10598 5.42654 2.49796C4.76601 1.84564 4.76601 1.96133 4.34016 2.13775C3.99345 2.28373 3.66084 2.46116 3.34649 2.66781C2.73109 3.07644 2.38975 3.41614 2.15098 3.92651C1.91138 4.43688 1.80471 5.63322 3.03797 7.87409C4.27123 10.115 5.13689 11.2604 6.9273 13.0467C8.71934 14.833 10.0962 15.7939 12.1098 16.9229C14.6009 18.3178 15.556 18.0462 16.068 17.8075C16.58 17.5695 16.9214 17.2306 17.3316 16.6152C17.5386 16.3013 17.716 15.969 17.8617 15.6224C18.0389 15.199 18.1546 15.199 17.5007 14.5385L17.4999 14.5376Z" stroke="#0578EC" strokeWidth="1.4"/>
                      </svg>
                      <span>+1 (714) 655-1681</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other Mortgage Programs Section */}
        <section className="reverse-programs">
          <div className="reverse-container">
            <div className="reverse-programs-header">
              <h2 className="reverse-programs-title">Other mortgage programs</h2>
              <p className="reverse-programs-description">
                Explore our comprehensive mortgage solutions designed to meet your unique financial needs and goals.
              </p>
            </div>
            <div className="reverse-program-cards-grid">
              <div className="reverse-program-card">
                <div className="reverse-program-card-content">
                  <div className="reverse-program-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 2L20 12L30 14L22 22L24 32L16 26L8 32L10 22L2 14L12 12L16 2Z" fill="#0578EC"/>
                    </svg>
                  </div>
                  <h3 className="reverse-program-title">Purchase<br />New Home</h3>
                  <p className="reverse-program-description">
                    Get expert assistance to secure the best mortgage rates and terms, guiding you through the entire home-buying process.
                  </p>
                  <Link to="/buy-home" className="reverse-program-cta">Get your rate</Link>
                </div>
              </div>
              <div className="reverse-program-card">
                <div className="reverse-program-card-content">
                  <div className="reverse-program-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 2L20 12L30 14L22 22L24 32L16 26L8 32L10 22L2 14L12 12L16 2Z" fill="#0578EC"/>
                    </svg>
                  </div>
                  <h3 className="reverse-program-title">Refinance<br />Home</h3>
                  <p className="reverse-program-description">
                    Refinance your existing mortgage to lower interest rates, adjust loan terms, or access your home's equity quickly.
                  </p>
                  <Link to="/refinance-home" className="reverse-program-cta">Get your rate</Link>
                </div>
              </div>
              <div className="reverse-program-card highlighted">
                <div className="reverse-program-card-content">
                  <div className="reverse-program-icon">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 2L20 12L30 14L22 22L24 32L16 26L8 32L10 22L2 14L12 12L16 2Z" fill="#FFFFFF"/>
                    </svg>
                  </div>
                  <h3 className="reverse-program-title">Reverse Mortgage</h3>
                  <p className="reverse-program-description">
                    A reverse mortgage allows homeowners 62+ to convert home equity into cash while staying in their property.
                  </p>
                  <Link to="/reverse-mortgage" className="reverse-program-cta">Get your rate</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ReverseMortgage;
